import { useEffect } from "react";
import {
  AlignItems,
  Box,
  ButtonSize,
  ButtonVariant,
  Field,
  Form,
  Input,
  PasswordInput,
  Label,
  ReadingOptimized,
  Space,
  TextAlign,
  FontWeight,
  PlainLink,
  HoverEffect,
} from "@gocardless/flux-react";
import { useForm } from "react-hook-form";
import { TemporaryAccessTokenCreateRequestBody } from "@gocardless/api/dashboard/types";
import { Trans } from "@lingui/macro";

import { Route, getRouteURL } from "src/common/routing";
import { SubmitButton } from "src/components/shared/SubmitButton";

interface Props {
  submitWithPassword: (
    requestBody: TemporaryAccessTokenCreateRequestBody
  ) => void;
  submitWithSso: (email: string) => void;
  isTargetedPasswordReset?: boolean;
  isSubmitting: boolean;
  usingSso: boolean;
  setSsoNotAvailable: (usingSso: boolean) => void;
}

export const EmailAndPassword: React.FC<Props> = ({
  submitWithPassword,
  submitWithSso,
  isTargetedPasswordReset,
  isSubmitting,
  usingSso,
  setSsoNotAvailable,
}) => {
  const defaultValues = {
    email: "",
    password: "",
  };

  const { register, handleSubmit, formState, reset, setValue } = useForm({
    defaultValues: defaultValues,
    mode: "onChange",
    shouldUnregister: true,
  });
  const { isDirty, isValid } = formState;
  const submitButtonDisabled = !!(
    !isDirty ||
    !isValid ||
    isSubmitting ||
    isTargetedPasswordReset
  );

  const handleEmailChange = (event: { target: { value: string } }) => {
    setSsoNotAvailable(false);
    // Call the normal react-hook-form handler
    setValue("email", event.target.value);
  };

  useEffect(() => {
    if (isTargetedPasswordReset) {
      reset();
    }
    // TODO: Fix exhaustive dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTargetedPasswordReset]);

  return (
    <ReadingOptimized alignText={AlignItems.FlexStart} textWidth="100%">
      <Form
        data-reading-optimized
        onSubmit={handleSubmit((data) =>
          usingSso ? submitWithSso(data.email) : submitWithPassword(data)
        )}
      >
        <Field>
          <Label htmlFor="email">
            <Trans id="Email address"> Email address </Trans>
          </Label>
          <Input
            type="email"
            id="email"
            autoComplete="email"
            {...register("email", {
              required: true,
              onChange: (e) => handleEmailChange(e),
            })}
          />
        </Field>
        <Space v={1.5} />
        {!usingSso && (
          <>
            <Field>
              <Label htmlFor="password">
                <Trans id="Password"> Password </Trans>
              </Label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                {...register("password", { required: true })}
                showButtonLabel="Show password"
                hideButtonLabel="Hide password"
              />
            </Field>
            <Space v={1.5} />
          </>
        )}

        <Box
          width="100%"
          layout="flex"
          flexDirection={["column", "row", null, null]}
          alignItems={[null, AlignItems.Center, null, null]}
          spaceBelow={4}
        >
          <SubmitButton
            dataTestId="sign-in-button"
            variant={ButtonVariant.PrimaryOnLight}
            size={[ButtonSize.Md, null, null, ButtonSize.Lg]}
            disabled={submitButtonDisabled}
            isSubmitting={isSubmitting}
          >
            <Trans id="Sign in"> Sign in </Trans>
          </SubmitButton>
          <Box gutterH={[0, 1.5, null, null]} gutterV={[1, 0, null, null]} />
          {!usingSso && (
            <PlainLink
              href={getRouteURL({
                route: Route.ForgotPassword,
              })}
              effect={HoverEffect.TextDecoration}
              textAlign={TextAlign.Center}
              weight={FontWeight.SemiBold}
            >
              <Trans id="Forgotten password-question">
                Forgotten password?
              </Trans>
            </PlainLink>
          )}
        </Box>
      </Form>
    </ReadingOptimized>
  );
};
